import { useContext, useState } from 'react';
import { Button, Chip, Divider, Grid, Typography } from '@mui/material';
import { Publish } from '@mui/icons-material';
import { format } from 'date-fns';

import { useTranslation } from 'react-i18next';
import {
    SttDialog,
    SttFormText,
    SttFormDate,
    SttFormTime,
    SttFormCheck,
    FieldHelpText,
    UploadFileDialog,
    AppContext,
} from '../all';
import useActivitiesService from '../../services/ActivitiesService';
import { CreateDuplicationActivitiesBody } from '../../types/api/paths/Student';
import {
    isRenewalPreenrollmentVisible,
    isStudentRelocationVisible,
} from '../../config/features';
import { isErrorResponse } from '../../types/common';
import { parseToDate, safeFormatDate } from '../../lib/utils';
import { useSingleFlight } from '../../lib/useSingleFlight';

/** Time used when a pre-enrollment date is picked while its field was still empty. */
const PREENROLLMENT_DEFAULT_TIME: Record<string, string> = {
    preenrollmentIni: '00:00:00',
    preenrollmentEnd: '23:59:00',
};

type PreenrollmentBound = 'preenrollmentIni' | 'preenrollmentEnd';

type SttActivitiesDuplicationDialogProps = {
    open?: boolean;
    onClose: () => void;
    onDuplicate: () => void;
    activities: number[];
};

type ActivityDocument = {
    id: number;
    url: string;
    name: string;
};

type FormChange = {
    name: string | undefined;
    value: unknown;
};

/**
 * Activities duplication dialog
 */
export function SttActivitiesDuplicationDialog({
    open = false,
    onClose,
    onDuplicate,
    activities,
}: SttActivitiesDuplicationDialogProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [form, setForm] = useState<CreateDuplicationActivitiesBody>({
        ids: activities,
        originalNameFragment: undefined,
        newNameFragment: undefined,
        inscIni: undefined,
        inscEnd: undefined,
        timeini: undefined,
        timeend: undefined,
        allowPreenrollment: false,
        relocateStudents: false,
    });
    const [documents, setDocuments] = useState<ActivityDocument[]>([]);
    const [openUploadPoster, setOpenUploadPoster] = useState(false);
    const [showRelocationNotice, setShowRelocationNotice] = useState(false);
    const { useCreateDuplicationsActivities } = useActivitiesService();
    const duplicateActivitiesMutation = useCreateDuplicationsActivities();

    /** Duplicates once per click burst: a second click while saving is ignored. */
    const duplicate = useSingleFlight(async () => {
        if (!isPreenrollmentWindowValid()) {
            return;
        }

        try {
            await duplicateActivitiesMutation.mutateAsync({
                params: {
                    ...form,
                    idFiles: documents?.map((doc: ActivityDocument) => doc.id),
                },
            });
        } catch (error) {
            if (isErrorResponse(error)) {
                cxt.showMessage('E', error.error.msg);
            }
            return;
        }

        // Relocating goes on in the background: say where to follow it.
        if (form.relocateStudents) {
            setShowRelocationNotice(true);
            return;
        }

        cxt.showMessage('S', t('generic.operationSuccessful'));
        onDuplicate();
    });

    /** Keeps the time half when a date is picked, or seeds the bound's default. */
    function setPreenrollmentDate(name: PreenrollmentBound, date: unknown) {
        const parsedDate = parseToDate(date as string);

        if (!parsedDate) {
            setForm({ ...form, [name]: undefined });
            return;
        }

        const currentDate = parseToDate(form[name]);
        const time = currentDate
            ? format(currentDate, 'HH:mm:ss')
            : (PREENROLLMENT_DEFAULT_TIME[name] ?? '00:00:00');

        setForm({
            ...form,
            [name]: `${format(parsedDate, 'yyyy-MM-dd')} ${time}`,
        });
    }

    /** Sets the time half of a bound. Ignored while its date is empty. */
    function setPreenrollmentTime(name: PreenrollmentBound, time: string) {
        const currentDate = parseToDate(form[name]);

        if (!currentDate) {
            return;
        }

        setForm({
            ...form,
            [name]: `${format(currentDate, 'yyyy-MM-dd')} ${time}`,
        });
    }

    /**
     * Las mismas dos reglas que guarda el detalle: la ventana no puede acabar
     * antes de empezar ni solaparse con la inscripción de las copias. Avisa y
     * devuelve `false` para no llamar a la API con fechas que va a rechazar.
     */
    function isPreenrollmentWindowValid(): boolean {
        const preenrollmentIniDate = parseToDate(form.preenrollmentIni);
        const preenrollmentEndDate = parseToDate(form.preenrollmentEnd);

        if (
            preenrollmentIniDate &&
            preenrollmentEndDate &&
            preenrollmentIniDate >= preenrollmentEndDate
        ) {
            cxt?.showMessage('E', t('activity.preenrollment.invalidPeriod'));
            return false;
        }

        const inscIniDate = parseToDate(form.inscIni);
        const inscEndDate = parseToDate(form.inscEnd);

        // Pegarse no es solaparse: la ventana puede acabar cuando abre la
        // inscripción general.
        if (
            preenrollmentIniDate &&
            preenrollmentEndDate &&
            inscIniDate &&
            inscEndDate &&
            preenrollmentIniDate < inscEndDate &&
            inscIniDate < preenrollmentEndDate
        ) {
            cxt?.showMessage('E', t('activity.preenrollment.overlapsSignup'));
            return false;
        }

        return true;
    }

    if (!open) {
        return null;
    }

    if (showRelocationNotice) {
        return (
            <SttDialog
                open
                title={t('activity.relocation.savedTitle')}
                onClose={() => onDuplicate()}
                maxWidth="sm"
                content={
                    <Typography>
                        {t('activity.relocation.savedHint')}
                    </Typography>
                }
            />
        );
    }

    return (
        <SttDialog
            open
            title={t('duplicateActivities')}
            onClose={() => onClose()}
            maxWidth="md"
            content={
                <>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormText
                                name="originalNameFragment"
                                caption={t('activity.currentNameToChange')}
                                defVal={form.originalNameFragment || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        originalNameFragment: value as string,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.currentNameToChangeHelp" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormText
                                name="newNameFragment"
                                caption={t('activity.newActivityName')}
                                defVal={form.newNameFragment || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        newNameFragment: value as string,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.newActivityNameHelp" />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormDate
                                name="inscIni"
                                caption={t('activity.inscriptionFrom')}
                                defVal={form.inscIni || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        inscIni: (value as string) ?? undefined,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.inscriptionFromHelp" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormDate
                                name="inscEnd"
                                caption={t('activity.inscriptionUntil')}
                                defVal={form.inscEnd || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        inscEnd: (value as string) ?? undefined,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.inscriptionUntilHelp" />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormDate
                                name="timeini"
                                caption={t('activity.durationFrom')}
                                defVal={form.timeini || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        timeini: (value as string) ?? undefined,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.durationFromHelp" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SttFormDate
                                name="timeend"
                                caption={t('activity.durationUntil')}
                                defVal={form.timeend || ''}
                                onChange={({ value }: FormChange) => {
                                    setForm({
                                        ...form,
                                        timeend: (value as string) ?? undefined,
                                    });
                                }}
                            />
                            <FieldHelpText i18nKey="activity.duplication.durationUntilHelp" />
                        </Grid>

                        {/* Renewal pre-enrollment: arms the window, dates are set in the activity detail.
                            Hidden in production while product validates it. */}
                        {isRenewalPreenrollmentVisible() && (
                            <Grid size={{ xs: 12 }}>
                                <SttFormCheck
                                    name="allowPreenrollment"
                                    color="primary"
                                    caption={t(
                                        'activity.duplication.allowPreenrollment',
                                    )}
                                    checked={!!form.allowPreenrollment}
                                    onChange={({ value }: FormChange) => {
                                        const allowed = value as boolean;
                                        setForm({
                                            ...form,
                                            allowPreenrollment: allowed,
                                            // Sin casilla no hay ventana: las
                                            // fechas se van con ella.
                                            ...(allowed
                                                ? {}
                                                : {
                                                      preenrollmentIni:
                                                          undefined,
                                                      preenrollmentEnd:
                                                          undefined,
                                                  }),
                                        });
                                    }}
                                />
                                <FieldHelpText
                                    i18nKey="activity.duplication.allowPreenrollmentHelp"
                                    className="ml-8"
                                />
                            </Grid>
                        )}

                        {/* La ventana se configura aquí para todas las copias, con la
                            misma forma que el bloque del detalle de la actividad. */}
                        {isRenewalPreenrollmentVisible() &&
                            !!form.allowPreenrollment && (
                                <Grid size={{ xs: 12 }}>
                                    <div className="rounded-lg border border-gray-300 p-4">
                                        <Typography variant="subtitle1">
                                            {t('activity.preenrollment.title')}
                                        </Typography>
                                        <FieldHelpText i18nKey="activity.duplication.preenrollmentPeriodHelp" />

                                        <Grid
                                            container
                                            spacing={2}
                                            className="mt-3"
                                        >
                                            <Grid size={{ xs: 6, sm: 3 }}>
                                                <SttFormDate
                                                    name="preenrollmentIni"
                                                    caption={t(
                                                        'activity.preenrollment.signupIni',
                                                    )}
                                                    defVal={
                                                        form.preenrollmentIni ??
                                                        ''
                                                    }
                                                    onChange={({
                                                        value,
                                                    }: FormChange) =>
                                                        setPreenrollmentDate(
                                                            'preenrollmentIni',
                                                            value,
                                                        )
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 3 }}>
                                                <SttFormTime
                                                    name="preenrollmentIni"
                                                    defVal={safeFormatDate(
                                                        form.preenrollmentIni,
                                                        'HH:mm',
                                                    )}
                                                    disabled={
                                                        !form.preenrollmentIni
                                                    }
                                                    min="00:00:00"
                                                    max="23:45"
                                                    interval={15}
                                                    onChange={({
                                                        value,
                                                    }: {
                                                        value: string;
                                                    }) =>
                                                        setPreenrollmentTime(
                                                            'preenrollmentIni',
                                                            value,
                                                        )
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 3 }}>
                                                <SttFormDate
                                                    name="preenrollmentEnd"
                                                    caption={t(
                                                        'activity.preenrollment.signupEnd',
                                                    )}
                                                    defVal={
                                                        form.preenrollmentEnd ??
                                                        ''
                                                    }
                                                    onChange={({
                                                        value,
                                                    }: FormChange) =>
                                                        setPreenrollmentDate(
                                                            'preenrollmentEnd',
                                                            value,
                                                        )
                                                    }
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6, sm: 3 }}>
                                                <SttFormTime
                                                    name="preenrollmentEnd"
                                                    defVal={safeFormatDate(
                                                        form.preenrollmentEnd,
                                                        'HH:mm',
                                                    )}
                                                    disabled={
                                                        !form.preenrollmentEnd
                                                    }
                                                    min="00:00:00"
                                                    max="23:45"
                                                    interval={15}
                                                    onChange={({
                                                        value,
                                                    }: {
                                                        value: string;
                                                    }) =>
                                                        setPreenrollmentTime(
                                                            'preenrollmentEnd',
                                                            value,
                                                        )
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>
                                </Grid>
                            )}

                        {isStudentRelocationVisible() && (
                            <Grid size={{ xs: 12 }}>
                                <SttFormCheck
                                    name="relocateStudents"
                                    color="primary"
                                    caption={t(
                                        'activity.duplication.relocateStudents',
                                    )}
                                    checked={!!form.relocateStudents}
                                    onChange={({ value }: FormChange) => {
                                        setForm({
                                            ...form,
                                            relocateStudents: value as boolean,
                                        });
                                    }}
                                />
                                <FieldHelpText
                                    i18nKey="activity.duplication.relocateStudentsHelp"
                                    className="ml-8"
                                />
                            </Grid>
                        )}

                        <Grid size={{ xs: 12 }}>
                            <Divider />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle1">
                                {t('generic.documents')}
                            </Typography>
                            <FieldHelpText i18nKey="activity.duplication.documentsHelp" />
                            {documents.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {documents.map(
                                        (document: ActivityDocument) => (
                                            <Chip
                                                key={document.id}
                                                label={document.name}
                                                variant="filled"
                                                color="primary"
                                                size="medium"
                                            />
                                        ),
                                    )}
                                </div>
                            )}
                        </Grid>
                    </Grid>
                    <UploadFileDialog
                        open={openUploadPoster}
                        types={['ACTIVITY_DOC'] as any}
                        onClose={() => setOpenUploadPoster(false)}
                        onUploaded={(r: any) => {
                            const { file } = r;
                            setOpenUploadPoster(false);
                            setDocuments([
                                ...documents,
                                {
                                    id: file?.id,
                                    url: file?.url,
                                    name: file?.name,
                                },
                            ]);
                        }}
                    />
                </>
            }
            buttons={[
                {
                    type: 'component',
                    show: true,
                    component: (
                        <Button
                            size="medium"
                            color="inherit"
                            variant="contained"
                            startIcon={<Publish />}
                            onClick={() => setOpenUploadPoster(true)}
                        >
                            {t('activity.uploadDocuments')}
                        </Button>
                    ),
                },
                {
                    type: 'save',
                    loading: duplicateActivitiesMutation.isPending,
                    disabled: duplicateActivitiesMutation.isPending,
                    onClick: () => {
                        void duplicate();
                    },
                },
            ]}
        />
    );
}
